// Data layer for the review screen.
//
// HTTP IS THE DEFAULT AS OF WAVE 3, AND THAT IS THE WHOLE POINT. This file used
// to default to fixture mode, so the document id in the URL was not the document
// on screen: every "the review screen works" observation ever made — including
// the PRD 17 §1 fix that made the route reachable — was against `FIXTURE_DOC`
// with the URL's id pasted over it. Walkthrough beat 9's "persists across a
// reload" was structurally unreachable. Tests opt back in with
// `window.__MAISIGHT_REVIEW_API = "fixture"`.
//
// TWO ENDPOINTS, BECAUSE THE PAYLOADS DISAGREED AND THE BACKEND IS RIGHT.
// `GET /docs/:id/review` returns `{doc_id, proposed, review_state}`; this file
// expected `{doc, glossary_proposed, review_state}` and so rendered "Failed to
// load review" against a 200 carrying real data. The backend shape is the one
// with other consumers, so the frontend adapts. It carries no `DocJSON` at all,
// and nothing serves `doc.json` on its own, so the document body comes from
// `GET /docs/:id/render` — which 409s until a doc is published. See
// `emptyDoc()` for what that costs.

import { useEffect, useRef, useState, useCallback } from "react";
import { apiFetch } from "../lib/apiClient";
import { FIXTURE_DOC, FIXTURE_GLOSSARY } from "../data/fixture";
import type { DocJSON } from "../data/types";
import type {
  ProposedGlossary,
  PublishResponse,
  ReviewResponse,
  ReviewStatePayload,
} from "./types";
import { useReviewStore } from "./store";

export type ApiMode = "fixture" | "http";

declare global {
  interface Window {
    __MAISIGHT_REVIEW_API?: ApiMode;
    __MAISIGHT_LAST_SAVE?: ReviewStatePayload;
  }
}


// `apiFetch` resolves paths through `apiUrl`, which is absolute for the same
// reason this file used to build its own: `/docs/:id/review` is ALSO an SPA
// route, so a bare relative fetch resolves against the app origin and gets
// the HTML shell back. It also attaches the credential these routes now
// require, which is why the local `fetch` wrapper is gone.

function currentMode(): ApiMode {
  if (typeof window === "undefined") return "fixture";
  return window.__MAISIGHT_REVIEW_API ?? "http";
}

/** The shape `backend/api/routes/docs.py::get_review` actually returns. */
interface ServerReview {
  doc_id: string;
  proposed: ProposedGlossary;
  review_state?: ReviewStatePayload | null;
}

/**
 * A document that has never been published has no readable body: `/render`
 * answers 409 `unpublished` and no endpoint serves `doc.json`. Reviewing the
 * proposed terms still works; the preview pane is simply empty. Inventing
 * sections here would be worse than showing none. Recorded as a backend gap in
 * the Wave 3 progress notes.
 */
function emptyDoc(docId: string): DocJSON {
  return {
    doc_id: docId,
    pipeline_version: 0,
    format: "markdown",
    sections: [],
    citations: [],
  };
}

async function fetchReview(docId: string): Promise<ReviewResponse> {
  const id = encodeURIComponent(docId);
  const reviewRes = await apiFetch(`/docs/${id}/review`, {
    headers: { Accept: "application/json" },
  });
  if (!reviewRes.ok) throw new Error(`GET review failed: ${reviewRes.status}`);
  const server = (await reviewRes.json()) as ServerReview;

  const renderRes = await apiFetch(`/docs/${id}/render`, {
    headers: { Accept: "application/json" },
  });
  const doc = renderRes.ok
    ? ((await renderRes.json()) as { doc: DocJSON }).doc
    : emptyDoc(docId);

  return {
    doc,
    glossary_proposed: server.proposed,
    review_state: server.review_state ?? null,
  };
}

function fixtureReview(docId: string): ReviewResponse {
  // Treat FIXTURE_GLOSSARY as if it were the proposed glossary so the
  // review UI can be exercised without the backend.
  const proposed: ProposedGlossary = {
    doc_id: FIXTURE_GLOSSARY.doc_id,
    extraction_glossary_version: FIXTURE_GLOSSARY.extraction_glossary_version,
    terms: FIXTURE_GLOSSARY.terms.map((t) => {
      // Strip published-only metadata; keep the bare GlossaryEntry.
      const { review_state: _drop, ...entry } = t;
      return entry;
    }),
  };
  return {
    doc: { ...FIXTURE_DOC, doc_id: docId || FIXTURE_DOC.doc_id },
    glossary_proposed: proposed,
    review_state: null,
  };
}

// Loading state shape — flat by design. Phase 2a swaps for TanStack Query.
export interface QueryState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

export function useReview(docId: string): QueryState<ReviewResponse> {
  const [state, setState] = useState<QueryState<ReviewResponse>>({
    data: null,
    loading: true,
    error: null,
  });
  const hydrate = useReviewStore((s) => s.hydrate);

  useEffect(() => {
    let cancelled = false;
    const mode = currentMode();
    if (mode === "fixture") {
      const data = fixtureReview(docId);
      hydrate(data.glossary_proposed, data.review_state ?? null);
      setState({ data, loading: false, error: null });
      return () => { cancelled = true; };
    }
    setState({ data: null, loading: true, error: null });
    fetchReview(docId)
      .then((data) => {
        if (cancelled) return;
        hydrate(data.glossary_proposed, data.review_state ?? null);
        setState({ data, loading: false, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ data: null, loading: false, error: err });
      });
    return () => { cancelled = true; };
  }, [docId, hydrate]);

  return state;
}

// Debounced save. Subscribes to the store's `dirty` flag and POSTs a
// snapshot after `delayMs` of inactivity. Returns a `flush` function for
// tests + the Publish button which needs to ensure the latest state is on
// disk before the publish call.
export function useSaveReview(docId: string, delayMs = 400): { flush: () => Promise<void> } {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dirty = useReviewStore((s) => s.dirty);
  const snapshot = useReviewStore((s) => s.snapshot);
  const markSaving = useReviewStore((s) => s.markSaving);
  const markClean = useReviewStore((s) => s.markClean);

  const post = useCallback(async () => {
    const body = snapshot();
    markSaving(true);
    if (currentMode() === "fixture") {
      // Fixture mode stores the last save on window for tests.
      if (typeof window !== "undefined") window.__MAISIGHT_LAST_SAVE = body;
      markClean();
      return;
    }
    try {
      const r = await apiFetch(`/docs/${encodeURIComponent(docId)}/review`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(`POST review failed: ${r.status}`);
      markClean();
    } finally {
      markSaving(false);
    }
  }, [docId, snapshot, markSaving, markClean]);

  useEffect(() => {
    if (!dirty) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      post();
    }, delayMs);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [dirty, delayMs, post]);

  const flush = useCallback(async () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (useReviewStore.getState().dirty) await post();
  }, [post]);

  return { flush };
}

export function usePublish(docId: string): {
  publish: () => Promise<PublishResponse>;
  publishing: boolean;
  error: Error | null;
} {
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const publish = useCallback(async () => {
    setPublishing(true);
    setError(null);
    try {
      if (currentMode() === "fixture") {
        const res: PublishResponse = {
          doc_id: docId,
          published_at: new Date().toISOString(),
          published_version: 1,
        };
        return res;
      }
      const r = await apiFetch(
        `/docs/${encodeURIComponent(docId)}/publish`,
        { method: "POST" },
      );
      if (!r.ok) throw new Error(`Publish failed: ${r.status}`);
      return (await r.json()) as PublishResponse;
    } catch (err) {
      const e = err as Error;
      setError(e);
      throw e;
    } finally {
      setPublishing(false);
    }
  }, [docId]);

  return { publish, publishing, error };
}

